import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { ICreateDroneSubmissionRequest } from 'types/services/baseServiceTypes';

import { createServiceInstance } from '../baseService';
import {
  IGetDroneReferenceDataRequest,
  IGetDroneReferenceDataResponse,
  IGetDroneRateCardsRequest,
  IGetDroneRateCardsResponse,
  IDroneRegistrationSubmissionFormData,
  IDroneRegistrationSubmissionResponse,
  IGetIndividualDronesRequest,
  IGetIndividualDronesResponse,
  IGetOrganisationDronesRequest,
  IGetOrganisationDronesResponse,
  ISearchDroneSerialNumberRequest,
  ISearchDroneSerialNumberResponse
} from './droneServiceTypes';

/**
 * Drone service model
 *
 * @description Returns object with drone services
 * @typedef {object} IDroneService
 */
export interface IDroneService {
  getDroneReferenceData: (request?: IGetDroneReferenceDataRequest) => Promise<AxiosResponse<IGetDroneReferenceDataResponse>>;
  getDroneRateCards: (request?: IGetDroneRateCardsRequest) => Promise<AxiosResponse<IGetDroneRateCardsResponse>>;
  getIndividualDrones: (request: IGetIndividualDronesRequest) => Promise<AxiosResponse<IGetIndividualDronesResponse>>;
  getOrganisationDrones: (request: IGetOrganisationDronesRequest) => Promise<AxiosResponse<IGetOrganisationDronesResponse>>;
  submitDroneRegistration: (request: ICreateDroneSubmissionRequest<IDroneRegistrationSubmissionFormData>) => Promise<AxiosResponse<IDroneRegistrationSubmissionResponse>>;
  submitDroneRenewal: (request?: FormData) => Promise<AxiosResponse<IDroneRegistrationSubmissionResponse>>;
  searchDroneSerialNumber: (request: ISearchDroneSerialNumberRequest) => Promise<AxiosResponse<ISearchDroneSerialNumberResponse>>;
}

/**
 * Drone service hook
 *
 * @description Gives access to all services relating to drones
 */
const useDroneService = (): IDroneService => {
  const token = useSelector((state: any) => state.B2CSettings.items.accesstokenForms);
  const webappurlFormsApi = useSelector((state: any) => state.B2CSettings.items.webappurlFormsApi);

  const getInstance = () => createServiceInstance(`${webappurlFormsApi}/api/myCASA/Forms/drone`, token);

  /**
   * Get Drone reference data service
   *
   * @description Get all reference data related to drone forms
   * @param {IGetDroneReferenceDataRequest} request Service request
   */
  const getDroneReferenceData = (request?: IGetDroneReferenceDataRequest): Promise<AxiosResponse<IGetDroneReferenceDataResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IGetDroneReferenceDataResponse>('/GetReferenceDataTypes/v1', request);
  };

  /**
   * Get Drone rate cards service
   *
   * @description Get all rate cards related to drones
   * @param {IGetDroneRateCardsRequest} request Service request
   */
  const getDroneRateCards = (request?: IGetDroneRateCardsRequest): Promise<AxiosResponse<IGetDroneRateCardsResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IGetDroneRateCardsResponse>('/GetRateCards/v1', request);
  };

  /**
   * Get individual drones
   *
   * @description Get all drones linked to selected individual
   * @param {IGetIndividualDronesRequest} request Service request
   */
  const getIndividualDrones = (request: IGetIndividualDronesRequest) => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IGetIndividualDronesResponse>('/GetDronesIndividual/v1', request);
  };

  /**
   * Get Organisation drones
   *
   * @description Get all drones linked to selected organisation
   * @param {IGetOrganisationDronesRequest} request Service request
   */
  const getOrganisationDrones = (request: IGetOrganisationDronesRequest) => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IGetOrganisationDronesResponse>('/GetDronesOrganisation/v1', request);
  };

  /**
   * Drone registration submission
   *
   * @description Submit create submission request for Drone Registration
   * @param {ICreateDroneSubmissionRequest<IDroneRegistrationSubmissionFormData>} request Submission request
   */
  const submitDroneRegistration = (request: ICreateDroneSubmissionRequest<IDroneRegistrationSubmissionFormData>) => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IDroneRegistrationSubmissionResponse>('/submission/v1', request);
  };

  const submitDroneRenewal = (request?: FormData): Promise<AxiosResponse<IDroneRegistrationSubmissionResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IDroneRegistrationSubmissionResponse>('/dronesRenewalSubmission/v1', request,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Accept': 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      });
  };

  /**
   * Search drone serial number
   *
   * @description Search drone serial number
   * @param {ISearchDroneSerialNumberRequest} request request
   */
  const searchDroneSerialNumber = (request: ISearchDroneSerialNumberRequest) => {
    const axiosInstance = getInstance();

    return axiosInstance.post<ISearchDroneSerialNumberResponse>('/SearchDroneSerialNumber/v1', request);
  };

  return {
    getDroneReferenceData,
    getDroneRateCards,
    getIndividualDrones,
    getOrganisationDrones,
    submitDroneRegistration,
    submitDroneRenewal,
    searchDroneSerialNumber
  };
};

export default useDroneService;